import Image from 'components/Common/Image/Image'
import LoadingBox from 'components/Common/Loading/LoadingBox'
import AddToTripAction from 'tripPlanner/components/GenericDrawer/AddToTripAction'
import IconButton from 'LuxKit/Button/IconButton'
import BodyText from 'LuxKit/Typography/BodyText'
import Heading from 'LuxKit/Typography/Heading'
import LineAngleLeftIcon from 'LuxKit/Icons/line/LineAngleLeftIcon'
import LineAngleRightIcon from 'LuxKit/Icons/line/LineAngleRightIcon'
import LineMapMarkerIcon from 'LuxKit/Icons/line/LineMapMarkerIcon'
import clsx from 'clsx'
import { rem } from 'polished'
import { useCallback, useEffect, useMemo, useState, type MouseEvent } from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'
import type { TripChatUiComponentData } from '@luxuryescapes/contract-trip'
import GooglePlacePriceLevel, { shouldShowPriceLevel } from 'tripPlanner/components/GooglePlacePriceLevel'
import GooglePlaceRatingDisplay from 'tripPlanner/components/GooglePlaceRating/GooglePlaceRatingDisplay'
import type { SendMessageOptions } from 'tripPlanner/components/AddToTrip/useAddHotelToTrip'
import type { GetPlaceInfoResult, TripItineraryData } from 'tripPlanner/api/types'
import { mapPlaceImages } from 'tripPlanner/utils/placeImages'

export type GooglePlaceUIData = Extract<TripChatUiComponentData, { type: 'google_place' }>

const REVIEW_SUMMARY_MAX_LENGTH = 160
const COMPACT_CARD_WIDTH = 360
const WIDE_CARD_WIDTH = 460

interface Props {
  data: GooglePlaceUIData
  placeInfo?: GetPlaceInfoResult
  isLoading: boolean
  isError: boolean
  onPlaceClick: (data: { googlePlaceId: string; placeName: string }) => void
  itineraryData?: TripItineraryData
  onSendMessage?: (message: string, options?: SendMessageOptions) => void
  /** Whether photo carousel arrow icons should mirror in RTL. */
  arrowIconRtlFlip?: boolean
}

const Card = styled.article`
  position: relative;
  container-type: inline-size;
  width: min(${rem(602)}, 100%);
  border: ${rem(1)} solid ${props => props.theme.palette.neutral.default.five};
  border-radius: ${rem(12)};
  background: ${props => props.theme.palette.neutral.surface.normalBackground};
  color: ${props => props.theme.palette.neutral.surface.normalContrast};
  text-align: start;
  font-family: ${props => props.theme.font.primary.family};

  &:hover {
    border-color: ${props => props.theme.palette.neutral.default.three};
    box-shadow: ${props => props.theme.shadow.bottom.small};
  }

  &:focus-within {
    outline: ${rem(2)} solid ${props => props.theme.palette.brand.primary.normal};
    outline-offset: ${rem(2)};
  }
`

const CardLayout = styled.div`
  display: grid;
  grid-template-columns: ${rem(104)} minmax(0, 1fr);
  gap: ${rem(12)};
  min-height: ${rem(140)};
  padding: ${rem(12)} ${rem(14)};

  @container (max-width: ${rem(COMPACT_CARD_WIDTH)}) {
    grid-template-columns: ${rem(88)} minmax(0, 1fr);
    gap: ${rem(10)};
    min-height: ${rem(124)};
    padding: ${rem(10)} ${rem(12)};
  }

  @container (min-width: ${rem(WIDE_CARD_WIDTH)}) {
    grid-template-columns: ${rem(132)} minmax(0, 1fr);
    gap: ${rem(16)};
    min-height: ${rem(160)};
  }
`

const ImageFrame = styled.div`
  position: relative;
  width: 100%;
  aspect-ratio: 1;
  min-width: 0;
  overflow: hidden;
  border-radius: ${rem(8)};
  background: ${props => props.theme.palette.neutral.surface.lightBackground};

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`

const CarouselButton = styled(IconButton)`
  position: absolute;
  top: 50%;
  z-index: 2;
  box-shadow: ${props => props.theme.shadow.bottom.medium};
  transform: translateY(-50%);
  transition: color 0.2s, background-color 0.2s, border-color 0.2s, opacity 0.2s;

  @media (hover: hover) {
    opacity: 0;
    pointer-events: none;

    ${Card}:hover &,
    ${Card}:has(:focus-visible) & {
      opacity: 0.72;
      pointer-events: auto;

      &:hover,
      &:focus-visible {
        opacity: 1;
      }
    }
  }
`

const PreviousButton = styled(CarouselButton)`
  inset-inline-start: ${rem(6)};
`

const NextButton = styled(CarouselButton)`
  inset-inline-end: ${rem(6)};
`

const EmptyGallery = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${props => props.theme.palette.neutral.default.six};
`

const Title = styled(Heading)`
  min-width: 0;

  &.with-action-space {
    padding-inline-end: ${rem(116)};
  }

  @container (max-width: ${rem(COMPACT_CARD_WIDTH)}) {
    &.with-action-space {
      padding-inline-end: ${rem(96)};
    }
  }

  @container (min-width: ${rem(WIDE_CARD_WIDTH)}) {
    &.with-action-space {
      padding-inline-end: ${rem(132)};
    }
  }
`

const ContentDetailsButton = styled.button`
  display: flex;
  flex-direction: column;
  gap: ${rem(16)};
  width: 100%;
  min-width: 0;
  border: 0;
  padding: 0;
  background: transparent;
  color: inherit;
  cursor: pointer;
  font: inherit;
  text-align: start;
`

const Header = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${rem(8)};
  min-width: 0;
`

const MetaRow = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: ${rem(10)};
  min-width: 0;
`

const MetaDivider = styled.span`
  width: ${rem(1)};
  height: ${rem(20)};
  background: ${props => props.theme.palette.neutral.default.five};
`

const ActionContainer = styled.div`
  position: absolute;
  top: ${rem(14)};
  inset-inline-end: ${rem(14)};
  z-index: 3;
`

const LoadingContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${rem(12)};
  padding-top: ${rem(6)};
`

const SummaryText = styled(BodyText)`
  &.review-summary {
    font-style: italic;
  }
`

const CategoryText = styled(BodyText)`
  min-width: 0;
`

function formatReviewSummary(reviewText: string): string {
  const trimmedReview = reviewText.trim()
  const capitalisedReview = `${trimmedReview.charAt(0).toUpperCase()}${trimmedReview.slice(1)}`
  if (capitalisedReview.length > REVIEW_SUMMARY_MAX_LENGTH) {
    const truncatedReview = capitalisedReview.slice(0, REVIEW_SUMMARY_MAX_LENGTH).trimEnd()
    const wordBoundaryIndex = truncatedReview.lastIndexOf(' ')
    const wordBoundaryReview = wordBoundaryIndex > 0 ? truncatedReview.slice(0, wordBoundaryIndex) : truncatedReview
    const cleanReview = wordBoundaryReview.replace(/[\s.,;:!?'"()[\]{}<>/\\|_-]+$/u, '')

    return `"${cleanReview}..."`
  }

  return `"${capitalisedReview}"`
}

function ChatGooglePlaceWidget({
  data,
  placeInfo,
  isLoading,
  isError,
  onPlaceClick,
  itineraryData,
  onSendMessage,
  arrowIconRtlFlip = false,
}: Props) {
  const intl = useIntl()
  const [activeImageIndex, setActiveImageIndex] = useState(0)
  const { googlePlaceId } = data

  const displayName = placeInfo?.displayName || placeInfo?.address || googlePlaceId
  const images = useMemo(
    () => mapPlaceImages(placeInfo?.imageIds, placeInfo?.images),
    [placeInfo?.imageIds, placeInfo?.images],
  )
  const review = placeInfo?.reviews?.find(item => item.text?.trim())
  const reviewText = review?.text?.trim()
  const isReviewSummary = !placeInfo?.description && !!reviewText
  const summary = placeInfo?.description || (reviewText ? formatReviewSummary(reviewText) : undefined)
  const activeImage = images[activeImageIndex]
  const rating = placeInfo?.rating
  const ratingsCount = placeInfo?.userRatingCount
  const priceLevel = placeInfo?.priceLevel
  const category = placeInfo?.category
  const showRating = typeof rating === 'number'
  const showPrice = shouldShowPriceLevel(priceLevel)
  const showCategory = !!category
  const hasMetadata = showRating || showPrice || showCategory
  const coordinates = useMemo(
    () => placeInfo ? { lat: placeInfo.lat, lng: placeInfo.lng } : undefined,
    [placeInfo],
  )
  const showAddToTrip = !!placeInfo && !!itineraryData && !!onSendMessage

  const fallbackLabel = intl.formatMessage({
    id: 'DfFbEoS8uWSkGKjf_c6A',
    defaultMessage: 'Open place details',
    description: 'Accessible label for a chat Google place card button',
  })

  const cardLabel = intl.formatMessage({
    id: '2XUbxWRuORJQKZqNhz7p',
    defaultMessage: 'Open place details for {placeName}',
    description: 'Accessible label for a chat Google place card button with the place name',
  }, { placeName: displayName })

  const unavailableText = intl.formatMessage({
    id: 'Fb82mI_dQggQ06nisC_S',
    defaultMessage: 'Place details are unavailable right now.',
    description: 'Fallback text shown on a Google place chat card when place detail loading fails',
  })

  const previousPhotoLabel = intl.formatMessage({
    id: 'dxOjyj8nerXgtUi8yUDj',
    defaultMessage: 'Previous place photo',
    description: 'Accessible label for the previous photo button on a Google place chat card',
  })

  const nextPhotoLabel = intl.formatMessage({
    id: 'n2ACMqzp6usvIP3LQFQH',
    defaultMessage: 'Next place photo',
    description: 'Accessible label for the next photo button on a Google place chat card',
  })

  const photoAltText = intl.formatMessage({
    id: 'tBLP-GlLWJqtBIaub0w1',
    defaultMessage: '{placeName} photo {photoNumber}',
    description: 'Alt text for a Google place photo shown on a chat card',
  }, {
    placeName: displayName,
    photoNumber: activeImageIndex + 1,
  })

  useEffect(() => {
    setActiveImageIndex(0)
  }, [googlePlaceId, images.length])

  const handleClick = useCallback(() => {
    onPlaceClick({ googlePlaceId, placeName: displayName })
  }, [onPlaceClick, googlePlaceId, displayName])

  const handlePreviousImageClick = useCallback((event: MouseEvent<HTMLButtonElement>) => {
    event.preventDefault()
    setActiveImageIndex(index => (index - 1 + images.length) % images.length)
  }, [images.length])

  const handleNextImageClick = useCallback((event: MouseEvent<HTMLButtonElement>) => {
    event.preventDefault()
    setActiveImageIndex(index => (index + 1) % images.length)
  }, [images.length])

  if (isLoading && !placeInfo) {
    return (
      <Card aria-label={fallbackLabel} aria-busy>
        <CardLayout>
          <ImageFrame>
            <EmptyGallery>
              <LoadingBox floating />
            </EmptyGallery>
          </ImageFrame>
          <LoadingContent>
            <LoadingBox floating />
            <LoadingBox floating />
          </LoadingContent>
        </CardLayout>
      </Card>
    )
  }

  return (
    <Card aria-label={cardLabel}>
      {showAddToTrip && (
        <ActionContainer>
          <AddToTripAction
            itineraryData={itineraryData}
            onSendMessage={onSendMessage}
            placeName={displayName}
            placeId={googlePlaceId}
            coordinates={coordinates}
            variant="outline"
          />
        </ActionContainer>
      )}
      <CardLayout>
        <ImageFrame>
          {activeImage && <Image image={activeImage} alt={photoAltText} fit="center" objectFit="cover" dpr={2} />}
          {!activeImage && (
            <EmptyGallery>
              <LineMapMarkerIcon size="L" colour="light-foreground-alt" />
            </EmptyGallery>
          )}
          {images.length > 1 && (
            <>
              <PreviousButton
                type="button"
                kind="secondary"
                variant="dark"
                shape="circle"
                size="small"
                aria-label={previousPhotoLabel}
                onClick={handlePreviousImageClick}
              >
                <LineAngleLeftIcon size="S" rtlFlip={arrowIconRtlFlip} />
              </PreviousButton>
              <NextButton
                type="button"
                kind="secondary"
                variant="dark"
                shape="circle"
                size="small"
                aria-label={nextPhotoLabel}
                onClick={handleNextImageClick}
              >
                <LineAngleRightIcon size="S" rtlFlip={arrowIconRtlFlip} />
              </NextButton>
            </>
          )}
        </ImageFrame>
        <ContentDetailsButton type="button" aria-label={cardLabel} onClick={handleClick}>
          <Header>
            <Title variant="heading6" className={clsx({ 'with-action-space': showAddToTrip })}>{displayName}</Title>
            {hasMetadata && (
              <MetaRow>
                {showRating && (
                  <GooglePlaceRatingDisplay
                    rating={rating}
                    ratingsCount={ratingsCount}
                    size="S"
                  />
                )}
                {showRating && showPrice && <MetaDivider />}
                {showPrice && <GooglePlacePriceLevel priceLevel={priceLevel} size="small" colour="normal-contrast" />}
                {(showRating || showPrice) && showCategory && <MetaDivider />}
                {showCategory && (
                  <CategoryText variant="small" colour="normal-contrast" lineClamp={1}>
                    {category}
                  </CategoryText>
                )}
              </MetaRow>
            )}
          </Header>
          {isError && !placeInfo && (
            <BodyText variant="medium" colour="light-foreground-alt">
              {unavailableText}
            </BodyText>
          )}
          {summary && (
            <SummaryText
              variant="medium"
              colour="normal-contrast"
              lineClamp={isReviewSummary ? undefined : 3}
              className={isReviewSummary ? 'review-summary' : undefined}
            >
              {summary}
            </SummaryText>
          )}
        </ContentDetailsButton>
      </CardLayout>
    </Card>
  )
}

export default ChatGooglePlaceWidget
